import React, { useState, useEffect, useCallback } from 'react';
import {
    getAuth,
    multiFactor,
    TotpMultiFactorGenerator,
    GoogleAuthProvider,
    EmailAuthProvider,
    signInWithPopup,
    reauthenticateWithCredential,
    sendPasswordResetEmail,
} from 'firebase/auth';
import Typography from '@material-ui/core/Typography';
import TextField from '@material-ui/core/TextField';
import Button from '@material-ui/core/Button';
import Paper from '@material-ui/core/Paper';
import CircularProgress from '@material-ui/core/CircularProgress';
import Stepper from '@material-ui/core/Stepper';
import Step from '@material-ui/core/Step';
import StepLabel from '@material-ui/core/StepLabel';
import Link from '@material-ui/core/Link';
import Alert from '@material-ui/lab/Alert';
import { QRCodeSVG } from 'qrcode.react';
import API, {
    GetUserMFATOTPSecretPathForApi,
    GetUserMFARecoveryCodesPathForApi,
} from '../../Util/api';
import RecoveryCodesDisplay from './RecoveryCodesDisplay';

const steps = ['Generate', 'Scan', 'Verify', 'Save'];

const MFAEnrollment = ({ onEnrollmentComplete, onCancel }) => {
    const [activeStep, setActiveStep] = useState(0);
    const [totpSecret, setTotpSecret] = useState(null);
    const [qrCodeUrl, setQrCodeUrl] = useState('');
    const [secretKey, setSecretKey] = useState('');
    const [verificationCode, setVerificationCode] = useState('');
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [requiresReauth, setRequiresReauth] = useState(false);
    const [reauthenticating, setReauthenticating] = useState(false);
    const [reauthPassword, setReauthPassword] = useState('');
    const [isGoogleUser, setIsGoogleUser] = useState(false);
    const [recoveryCodes, setRecoveryCodes] = useState([]);
    const [resetEmailSent, setResetEmailSent] = useState(false);
    const [resetEmailError, setResetEmailError] = useState('');

    const generateSecret = useCallback(async () => {
        setIsLoading(true);
        setError('');
        setRequiresReauth(false);
        try {
            const auth = getAuth();
            const user = auth.currentUser;
            if (!user) {
                setError('You must be signed in to enable MFA.');
                return;
            }

            const session = await multiFactor(user).getSession();
            const secret = await TotpMultiFactorGenerator.generateSecret(session);

            setTotpSecret(secret);
            setSecretKey(secret.secretKey);

            const userEmail = user.email || 'user';
            const url = secret.generateQrCodeUrl(userEmail, 'AP Central');
            setQrCodeUrl(url);

            setActiveStep(1);
        } catch (err) {
            console.error('Error generating TOTP secret:', err);
            if (err.code === 'auth/requires-recent-login' || err.code === 'auth/unsupported-first-factor') {
                const auth = getAuth();
                const user = auth.currentUser;
                const hasGoogle = user?.providerData.some(p => p.providerId === 'google.com');
                const hasPassword = user?.providerData.some(p => p.providerId === 'password');
                setIsGoogleUser(hasGoogle && !hasPassword);
                setRequiresReauth(true);
                if (err.code === 'auth/unsupported-first-factor') {
                    setError('Your current sign-in method doesn\'t support MFA directly. Please enter your password to continue.');
                } else {
                    setError('For security, please re-authenticate before enabling MFA.');
                }
            } else {
                setError(err.message || 'Failed to generate MFA secret. Please try again.');
            }
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        generateSecret();
    }, [generateSecret]);

    const handleVerify = async () => {
        if (verificationCode.length !== 6) {
            setError('Please enter a 6-digit code.');
            return;
        }

        setIsLoading(true);
        setError('');
        try {
            const auth = getAuth();
            const user = auth.currentUser;

            const assertion = TotpMultiFactorGenerator.assertionForEnrollment(
                totpSecret,
                verificationCode
            );

            await multiFactor(user).enroll(assertion, 'Authenticator App');

            // Store TOTP secret on backend for server-side verification
            // (needed for sign-in methods that bypass Firebase MFA, e.g. Intuit)
            try {
                await API.post(GetUserMFATOTPSecretPathForApi(), { secretKey: totpSecret.secretKey });
            } catch (secretErr) {
                console.error('Error storing TOTP secret:', secretErr);
            }

            // Generate recovery codes after successful enrollment
            try {
                const response = await API.post(GetUserMFARecoveryCodesPathForApi());
                setRecoveryCodes(response.data.recoveryCodes);
                setActiveStep(2);
            } catch (rcErr) {
                console.error('Error generating recovery codes:', rcErr);
                setError('MFA enabled, but failed to generate recovery codes. You can generate them from Account Security settings.');
                setActiveStep(3);
                if (onEnrollmentComplete) {
                    onEnrollmentComplete();
                }
            }
        } catch (err) {
            console.error('Error verifying TOTP code:', err);
            if (err.code === 'auth/invalid-verification-code') {
                setError('Invalid code. Please check your authenticator app and try again.');
            } else {
                setError(err.message || 'Failed to verify code. Please try again.');
            }
        } finally {
            setIsLoading(false);
        }
    };

    const handleRecoveryCodesAcknowledged = () => {
        setActiveStep(3);
        if (onEnrollmentComplete) {
            onEnrollmentComplete();
        }
    };

    const handleForgotPassword = async () => {
        setResetEmailError('');
        try {
            const auth = getAuth();
            const user = auth.currentUser;
            if (user?.email) {
                await sendPasswordResetEmail(auth, user.email);
                setResetEmailSent(true);
            }
        } catch (err) {
            console.error('Error sending password reset email:', err);
            setResetEmailError(err.message || 'Failed to send reset email.');
        }
    };

    const handleReauthenticate = async () => {
        setReauthenticating(true);
        setError('');

        try {
            const auth = getAuth();
            const user = auth.currentUser;

            if (isGoogleUser) {
                const provider = new GoogleAuthProvider();
                await signInWithPopup(auth, provider);
            } else {
                if (!reauthPassword) {
                    setError('Please enter your password.');
                    setReauthenticating(false);
                    return;
                }
                const credential = EmailAuthProvider.credential(user.email, reauthPassword);
                await reauthenticateWithCredential(user, credential);
                setReauthPassword('');
            }

            setRequiresReauth(false);
            await generateSecret();
        } catch (err) {
            console.error('Error re-authenticating:', err);
            if (err.code === 'auth/popup-closed-by-user') {
                setError('Sign-in cancelled. Please try again.');
            } else if (err.code === 'auth/wrong-password' || err.code === 'auth/invalid-credential') {
                setError('Incorrect password. Please try again.');
            } else {
                setError(err.message || 'Re-authentication failed. Please try again.');
            }
        } finally {
            setReauthenticating(false);
        }
    };

    return (
        <Paper elevation={3} style={{ padding: 32, maxWidth: 500, margin: '32px auto' }}>
            <Typography variant="h5" gutterBottom>
                Set Up Multi-Factor Authentication
            </Typography>

            <Stepper activeStep={activeStep} style={{ marginBottom: 24 }}>
                {steps.map((label) => (
                    <Step key={label}>
                        <StepLabel>{label}</StepLabel>
                    </Step>
                ))}
            </Stepper>

            {error && (
                <Alert severity="error" style={{ marginBottom: 16 }} onClose={() => setError('')}>
                    {error}
                </Alert>
            )}

            {activeStep === 0 && (
                <div style={{ textAlign: 'center', padding: '24px 0' }}>
                    {requiresReauth ? (
                        <div>
                            <Typography variant="body1" style={{ marginBottom: 24 }}>
                                For security, you need to re-authenticate before enabling MFA.
                            </Typography>
                            {!isGoogleUser && (
                                <>
                                    <TextField
                                        fullWidth
                                        type="password"
                                        label="Enter your password"
                                        value={reauthPassword}
                                        onChange={(e) => setReauthPassword(e.target.value)}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter' && reauthPassword) {
                                                handleReauthenticate();
                                            }
                                        }}
                                        autoFocus
                                        style={{ marginBottom: 8 }}
                                    />
                                    {resetEmailSent ? (
                                        <Alert severity="info" style={{ marginBottom: 16, textAlign: 'left' }}>
                                            Password reset email sent. Please check your inbox, reset your password, then sign out and sign back in to try again.
                                        </Alert>
                                    ) : (
                                        <div style={{ marginBottom: 16, textAlign: 'right' }}>
                                            <Link component="button" variant="body2" onClick={handleForgotPassword}>
                                                Forgot password?
                                            </Link>
                                            {resetEmailError && (
                                                <Typography variant="caption" color="error" style={{ display: 'block', marginTop: 4 }}>
                                                    {resetEmailError}
                                                </Typography>
                                            )}
                                        </div>
                                    )}
                                </>
                            )}
                            <div style={{ display: 'flex', gap: 16, justifyContent: 'center' }}>
                                {onCancel && (
                                    <Button
                                        variant="outlined"
                                        onClick={onCancel}
                                        disabled={reauthenticating}
                                    >
                                        Cancel
                                    </Button>
                                )}
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={handleReauthenticate}
                                    disabled={reauthenticating || (!isGoogleUser && !reauthPassword)}
                                >
                                    {reauthenticating ? <CircularProgress size={24} /> :
                                        isGoogleUser ? 'Re-authenticate with Google' : 'Re-authenticate'}
                                </Button>
                            </div>
                        </div>
                    ) : (
                        <div>
                            <CircularProgress />
                            <Typography style={{ marginTop: 16 }}>Generating your MFA secret...</Typography>
                        </div>
                    )}
                </div>
            )}

            {activeStep === 1 && (
                <div>
                    <Typography variant="body1" style={{ marginBottom: 16 }}>
                        Scan this QR code with your authenticator app (Google Authenticator, Authy, etc.):
                    </Typography>

                    <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 16 }}>
                        {qrCodeUrl && <QRCodeSVG value={qrCodeUrl} size={200} />}
                    </div>

                    <Typography variant="body2" color="textSecondary" style={{ marginBottom: 8 }}>
                        Can't scan? Enter this key manually:
                    </Typography>
                    <Paper variant="outlined" style={{ padding: 12, marginBottom: 24, fontFamily: 'monospace', wordBreak: 'break-all', textAlign: 'center' }}>
                        {secretKey}
                    </Paper>

                    <Typography variant="body1" style={{ marginBottom: 8 }}>
                        Enter the 6-digit code from your authenticator app:
                    </Typography>
                    <TextField
                        fullWidth
                        value={verificationCode}
                        onChange={(e) => {
                            const val = e.target.value.replace(/\D/g, '').slice(0, 6);
                            setVerificationCode(val);
                        }}
                        placeholder="000000"
                        inputProps={{ maxLength: 6, style: { textAlign: 'center', fontSize: '1.5rem', letterSpacing: '0.5rem' } }}
                        autoFocus
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' && verificationCode.length === 6) {
                                handleVerify();
                            }
                        }}
                        style={{ marginBottom: 16 }}
                    />

                    <div style={{ display: 'flex', gap: 16 }}>
                        {onCancel && (
                            <Button
                                variant="outlined"
                                onClick={onCancel}
                                disabled={isLoading}
                                fullWidth
                            >
                                Cancel
                            </Button>
                        )}
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleVerify}
                            disabled={isLoading || verificationCode.length !== 6}
                            fullWidth
                        >
                            {isLoading ? <CircularProgress size={24} /> : 'Verify & Enable'}
                        </Button>
                    </div>
                </div>
            )}

            {activeStep === 2 && (
                <RecoveryCodesDisplay
                    codes={recoveryCodes}
                    onAcknowledge={handleRecoveryCodesAcknowledged}
                />
            )}

            {activeStep === 3 && (
                <div style={{ textAlign: 'center', padding: '24px 0' }}>
                    <Alert severity="success" style={{ marginBottom: 16 }}>
                        Multi-factor authentication has been enabled successfully!
                    </Alert>
                    <Typography variant="body2" color="textSecondary">
                        You will now be asked for a code from your authenticator app each time you sign in.
                    </Typography>
                </div>
            )}
        </Paper>
    );
};

export default MFAEnrollment;
